/**
 * Shared contracts for booking-flow refresh-resume. A flow opts in by adding a
 * `persist` block to its `BookingFlowConfig` (see booking-flow/types.ts) and passing a
 * matching `ResumeAdapter` to `<BookingFlowResumer>` in its page.
 *
 * Split of responsibility:
 *  - `BookingFlowPersistConfig` — what/when to write to sessionStorage (module-agnostic
 *    bits the engine needs).
 *  - `ResumeAdapter` — how to reconstruct a live flow from a draft (the per-module bits:
 *    which state field carries the cart, how far a given cart is allowed to resume).
 */
import type { ShoppingCart } from '../api/types/shoppingCart';
import type { BookingFlowConfig } from './types';

export interface BookingFlowPersistConfig<TState> {
  /** `bookingDraftKey(config.id)`. */
  key: string;
  /** Bump on any partialize-shape / step-id / field-semantics change → old drafts discarded. */
  version: number;
  /** Serialisable subset to persist. MUST drop `searchResult`, raw `paymentForm`, and the
   *  full cart object (keep only its id). */
  partialize: (state: TState) => Partial<TState>;
  /** Earliest step id it's safe to resume into once a shopping cart exists — i.e. the
   *  first step after Allocate. Guarantees Allocate is never replayed on resume. */
  floorStepId: string;
  /** Where a "no cart yet, re-ran the search" resume lands (the results/allocate step). */
  searchResultStepId: string;
  /** Absolute-epoch deadline to carry in the draft (air post-search 15-min window). */
  getDeadline?: (state: TState) => number | undefined;
  /** Skip persisting when this returns false. Default: `stepIndex > 0 || partialize(state)
   *  is non-empty` (a fresh mount at step 0 writes nothing). */
  shouldPersist?: (state: TState, stepIndex: number) => boolean;
}

export interface ResumeAdapter<TState> {
  /** Pull the shopping-cart id out of a partialized draft, however this flow stores it. */
  getCartId: (draftState: Partial<TState>) => string | undefined;
  /** Merge a freshly re-fetched cart back into flow state under this flow's field name(s)
   *  (`shoppingCart`, `cart`, `shoppingCartId`, …). */
  applyResumedCart: (cart: ShoppingCart, draftState: Partial<TState>) => Partial<TState>;
  /** Given the re-fetched cart and the saved step, decide which step to resume at. This
   *  is the double-charge guard: a paid cart must never resolve to the payment step. */
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => string;
  /** Re-run this flow's (replay-safe) search step from the draft; null ⇒ not enough input.
   *  Typically re-calls the flow's search endpoint directly (avoids a config import cycle). */
  rerunSearch?: (draftState: Partial<TState>) => Promise<Partial<TState> | null>;
}

/** A config that is known to carry a `persist` block. */
export type PersistedBookingFlowConfig<TState> = BookingFlowConfig<TState> & {
  persist: BookingFlowPersistConfig<TState>;
};

/** Default "is this cart already paid?" test — mirrors PaymentReturnPage.tsx. Accepts any
 *  cart shape (the per-product `BusShoppingCart` / `TransferShoppingCart` narrows differ). */
export function cartIsPaid(
  cart: { remainingTotal?: number; shoppingSummary?: { paid?: boolean } } | null | undefined
): boolean {
  if (!cart) return false;
  if (cart.remainingTotal === 0) return true;
  return cart.shoppingSummary?.paid === true;
}

/** Drop `undefined`-valued keys so a partialize result reads as "empty" when nothing
 *  worth persisting has been entered yet (see `defaultShouldPersist`). */
export function compact<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

/** Clamp `n` into `[min, max]` (both inclusive). */
export function clampStepIndex(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(n, max));
}
